import React, { Component } from "react"
import { Form, FormGroup, Modal, ModalHeader, ModalBody, ModalFooter, Label, Button, Input, FormFeedback, Row, Col, UncontrolledTooltip } from "reactstrap"
import Select from "react-select"
import gql from 'graphql-tag'
import { graphql } from '@apollo/client/react/hoc'
import { AllCarModels, AllAgreementTypes } from "./Queries"
import { CreatePricingTemplateMutation } from './Mutations'
import toTitleCase from "../Functions/toTitleCase"
import withApolloClient from '../withApolloClient'
import PricingConfirmationModal from './PricingConfirmationModal'

const UserSelectedBranchesQuery = gql`
  query UserSelectedBranchesForPricing {
    currentUser {
      id
      member {
        id
        selectedBranches {
          edges {
            node {
              id
              name
              defaultMileageUnit
            }
          }
        }
      }
    }
  }
`

class AddPricingModal extends Component {
  constructor(props) {
    super(props)
    this.state = {
      input: {
        price: '',
        minimumPrice: '',
        maximumPrice: '',
        interval: '',
        intervalUnit: '',
        carYearMin: '',
        carYearMax: '',
        carMileageMin: '',
        carMileageMax: '',
        visibleToCustomers: false,
        isDefault: false,
        carModels: null,
        branchId: null,
        title: '',
      },
      defaultMileageUnit: "MI",
      createAnother: false,
      loading: false,
      error: "",
      selectedAgreementTypeOption: null,
      selectedBranchOption: null,
      selectedCarModels: null,
      showConfirmationModal: false,
    }
  }
  componentDidMount() {
    // Component initialization
  }

  resetForm = () => {
    this.setState({
      input: {
        price: '',
        minimumPrice: '',
        maximumPrice: '',
        interval: '',
        intervalUnit: '',
        carYearMin: '',
        carYearMax: '',
        carMileageMin: '',
        carMileageMax: '',
        visibleToCustomers: false,
        isDefault: false,
        carModels: null,
        branchId: null,
        title: '',
      },
      defaultMileageUnit: "MI",
      error: "",
      selectedAgreementTypeOption: null,
      selectedBranchOption: null,
      selectedCarModels: null,
    })
  }

  handleSubmit = (e) => {
    e.preventDefault()
    let input = this.state.input

    // Validate required fields
    if (!input.price || !input.minimumPrice || !input.maximumPrice || !input.interval || !input.intervalUnit) {
      this.setState({ loading: false, error: "Please provide all required fields:\nPrice, Minimum Price, Maximum Price, Interval, and Interval Unit!" })
      return
    }

    // Check if branch is selected (required first)
    if (!input.branchId) {
      this.setState({ loading: false, error: "Please select a Branch first!" })
      return
    }

    // Check if agreement type is selected
    if (!this.state.selectedAgreementTypeOption) {
      this.setState({ loading: false, error: "Please select an Agreement Type!" })
      return
    }

    // Require at least one car attribute for all pricing templates
    if (!input.carModels && !input.carYearMin && !input.carYearMax && !input.carMileageMin && !input.carMileageMax) {
      this.setState({ loading: false, error: "Please provide at least one of the following:\nCar Model, Car Year Min, Car Year Max, Car Mileage Min, Car Mileage Max!" })
      return
    }

    // Validate year range
    if (input.carYearMin && input.carYearMax && input.carYearMin > input.carYearMax) {
      this.setState({ loading: false, error: "Car Year Min must be less than Car Year Max!" })
      return
    }

    // Validate mileage range
    if (input.carMileageMin && input.carMileageMax && parseInt(input.carMileageMin) > parseInt(input.carMileageMax)) {
      this.setState({ loading: false, error: "Car Mileage Min must be less than Car Mileage Max!" })
      return
    }

    // If validation passes, show confirmation modal
    this.setState({ showConfirmationModal: true, error: "" })
  };

  handleConfirmCreate = () => {
    let input = this.state.input

    try {
      input = {
        ...input,
        price: parseFloat(input.price),
        minimumPrice: parseFloat(input.minimumPrice),
        maximumPrice: parseFloat(input.maximumPrice),
        interval: parseInt(input.interval),
        carYearMin: parseInt(input.carYearMin),
        carYearMax: parseInt(input.carYearMax),
        carMileageMin: input.carMileageMin ? { mileage: parseFloat(input.carMileageMin), mileageUnit: this.state.defaultMileageUnit === "KM" ? "KM" : "MI" } : null,
        carMileageMax: input.carMileageMax ? { mileage: parseFloat(input.carMileageMax), mileageUnit: this.state.defaultMileageUnit === "KM" ? "KM" : "MI" } : null,
      }
      this.setState({ loading: true })

      this.props.apolloClient.mutate({
        mutation: CreatePricingTemplateMutation,
        variables: { input },
      }).then((result) => {
        if (result.data && result.data.createPricingTemplate && result.data.createPricingTemplate.ok) {
          this.props.refetch()
          this.setState({ showConfirmationModal: false, loading: false, error: "" })
          if (!this.state.createAnother) {
            this.props.toggleModal()
          } else {
            this.resetForm()
            this.setState({ createAnother: false })
          }
        } else if (result.data && result.data.createPricingTemplate && !result.data.createPricingTemplate.ok && result.data.createPricingTemplate.errors && result.data.createPricingTemplate.errors[0]) {
          this.setState({ loading: false, showConfirmationModal: false, error: result.data.createPricingTemplate.errors[0].messages.toString() })
        } else {
          this.setState({ loading: false, showConfirmationModal: false, error: "Something went wrong, please contact admin for support!" })
        }
      }).catch((err) => {
        this.setState({ loading: false, showConfirmationModal: false, error: err.message })
      })

    } catch (err) {
      this.setState({ loading: false, error: err })
    }
  };

  handleChange = (e, number = false) => {
    if (e.target.type === "checkbox")
      this.setState({ input: { ...this.state.input, [e.target.name]: !this.state.input[e.target.name] } })
    else if (number) {
      this.setState({ input: { ...this.state.input, [e.target.name]: parseFloat(e.target.value) } })
    }
    else
      this.setState({ input: { ...this.state.input, [e.target.name]: e.target.value } })
  }

  handleBranchChange = (branch) => {
    if (branch) {
      this.setState({
        input: { ...this.state.input, branchId: branch.value, agreementType: null },
        selectedBranchOption: branch,
        selectedAgreementTypeOption: null, // Reset agreement type when branch changes
        defaultMileageUnit: branch.defaultMileageUnit || "MI"
      })
    } else {
      this.setState({
        input: { ...this.state.input, branchId: null, agreementType: null },
        selectedBranchOption: null,
        selectedAgreementTypeOption: null,
        defaultMileageUnit: "MI"
      })
    }
  };

  buildPricingSummary = () => {
    const { input, selectedAgreementTypeOption, selectedBranchOption, defaultMileageUnit } = this.state
    const { allCarModels } = this.props

    const selectedCarModelNames = input.carModels && allCarModels?.edges
      ? allCarModels.edges
        .filter(edge => input.carModels.includes(edge.node.id))
        .map(edge => {
          const item = edge.node
          return toTitleCase(item.make) + " " + item.name +
            (item.series ? " | " + item.series : "") +
            (item.groupType ? " | " + item.groupType.name : " | No Group Selected")
        })
      : []

    const isFallback = selectedAgreementTypeOption?.value === "__ALL__"

    const pricingType = isFallback
      ? "All Agreement Types"
      : selectedAgreementTypeOption?.label || "Unknown"

    const branch = selectedBranchOption?.label || null

    return {
      isFallback,
      pricingType,
      branch,
      price: `$${input.price} per ${input.interval} ${input.intervalUnit}`,
      priceRange: `$${input.minimumPrice} - $${input.maximumPrice}`,
      title: input.title || null,
      carModels: selectedCarModelNames,
      yearRange: input.carYearMin != null || input.carYearMax != null
        ? `${input.carYearMin ?? 'Any'} - ${input.carYearMax ?? 'Any'}`
        : null,
      mileageRange: input.carMileageMin != null || input.carMileageMax != null
        ? `${input.carMileageMin ?? 0} - ${input.carMileageMax ?? 'Any'} ${defaultMileageUnit}`
        : null,
      visibleToCustomers: input.visibleToCustomers,
      isDefault: input.isDefault
    }
  };

  handleAgreementTypeChange = (agreementType) => {
    // Since branch is always selected first, we just need to set agreementType
    // null for "All Agreement Types", or the specific value for a specific type
    this.setState({
      input: {
        ...this.state.input,
        agreementType: agreementType?.value === "__ALL__" ? null : agreementType?.value
      },
      selectedAgreementTypeOption: agreementType,
    })
  };

  handleCarModelChange = (selectedModels) => {
    const carModelIds = selectedModels?.length > 0
      ? selectedModels.map(model => model.value)
      : null

    this.setState({
      selectedCarModels: selectedModels,
      input: {
        ...this.state.input,
        carModels: carModelIds
      }
    })
  };


  render() {
    return (
      <Modal isOpen={this.props.isOpen} className="bos-car-attr-modal">
        <ModalHeader>Add New Pricing Templates</ModalHeader>
        <ModalBody>
          <Form onSubmit={this.handleSubmit}>
            <Row>
              <Col xs={4}>
                <FormGroup>
                  <Label for="templatePrice">Default Price *</Label>
                  <Input type="number" step="any" name="price" id="templatePrice" placeholder="$ Price" min={0} value={this.state.input.price} onChange={(e) => this.handleChange(e, true)} />
                </FormGroup>
              </Col>
              <Col xs={4}>
                <FormGroup>
                  <Label for="templateMinimumPrice">Minimum Price *</Label>
                  <Input valid={this.state.input.price && this.state.input.minimumPrice && this.state.input.minimumPrice < this.state.input.price}
                    invalid={this.state.input.price && this.state.input.minimumPrice && this.state.input.minimumPrice > this.state.input.price}
                    type="number" step="any" name="minimumPrice" id="templateMinimumPrice" placeholder="$ Minimum Price" min={0}
                    value={this.state.input.minimumPrice} onChange={(e) => this.handleChange(e, true)} />
                  <FormFeedback>Minimum Price must be less than the default Price</FormFeedback>
                </FormGroup>
              </Col>
              <Col xs={4}>
                <FormGroup>
                  <Label for="templatemaximumPrice">Maximum Price *</Label>
                  <Input
                    valid={this.state.input.price && this.state.input.maximumPrice && this.state.input.maximumPrice > this.state.input.price}
                    invalid={this.state.input.price && this.state.input.maximumPrice && this.state.input.maximumPrice < this.state.input.price}
                    type="number" step="any" name="maximumPrice" id="templatemaximumPrice" placeholder="$ Maximum Price" min={0}
                    value={this.state.input.maximumPrice} onChange={(e) => this.handleChange(e, true)} />
                  <FormFeedback>Maximum Price must be higher than the default Price</FormFeedback>
                </FormGroup>
              </Col>
              <Col xs={6}>
                <FormGroup>
                  <Label for="title">Pricing Template Title</Label>
                  <Input type="string" name="title" id="title" placeholder="Pricing Template Title" value={this.state.input.title} onChange={this.handleChange} />
                </FormGroup>
              </Col>
              <Col xs={6}>
                <FormGroup>
                  <Label for="branch">Branch *</Label>
                  <Select
                    isLoading={this.props.loadingUserBranches}
                    options={
                      this.props.userSelectedBranches?.edges?.map(({ node }) => ({
                        value: node.id,
                        label: node.name,
                        defaultMileageUnit: node.defaultMileageUnit || "MI"
                      })) || []
                    }
                    placeholder="Select Branch First"
                    value={this.state.selectedBranchOption}
                    onChange={this.handleBranchChange}
                  />
                </FormGroup>
              </Col>
              <Col xs={12}>
                <FormGroup>
                  <Label for="agreementType">Agreement Type *</Label>
                  <Select
                    isLoading={!(this.props.allAgreementTypes && this.props.allAgreementTypes.edges && this.props.allAgreementTypes.edges.length > 0)}
                    isDisabled={!this.state.selectedBranchOption}
                    options={
                      this.props.allAgreementTypes &&
                      this.props.allAgreementTypes.edges &&
                      this.props.allAgreementTypes.edges.length > 0 &&
                      this.state.selectedBranchOption &&
                      (() => {
                        // Filter agreement types by selected branch
                        const filteredAgreementTypes = this.props.allAgreementTypes.edges
                          .filter((item) => item.node.branch && item.node.branch.id === this.state.selectedBranchOption.value)
                          .map((item) => ({
                            value: item.node.id,
                            label: item.node.name,
                            branch: item.node.branch || null,
                            branchId: item.node.branch ? item.node.branch.id : null,
                            defaultMileageUnit: (item.node.branch && item.node.branch.defaultMileageUnit) || "MI",
                          }));

                        // Create "All Agreement Types" option
                        const allAgreementTypeOption = {
                          value: "__ALL__",
                          label: "All Agreement Types",
                        };

                        // Return with "All" at the top
                        return [allAgreementTypeOption, ...filteredAgreementTypes];
                      })()
                    }
                    placeholder={this.state.selectedBranchOption ? "Select Agreement Type" : "Select Branch First"}
                    value={this.state.selectedAgreementTypeOption}
                    onChange={(agreementType) => this.handleAgreementTypeChange(agreementType)}
                  />
                </FormGroup>
              </Col>
              <Col xs={6}>
                <FormGroup>
                  <Label for="templateInterval">Interval *</Label>
                  <Input type="number" name="interval" id="templateInterval" placeholder="Interval" min="1" max="9" value={this.state.input.interval} onChange={this.handleChange} />
                </FormGroup>
              </Col>
              <Col xs={6}><FormGroup>
                <Label style={{ display: "block", marginBottom: "10px" }} for="templateIntervalUnit">Interval Unit *</Label>
                <Input style={{ padding: "5px", display: "block", marginBottom: "10px", boxSizing: "border-box", width: "100%", borderRadius: "6px" }} type="select" name="intervalUnit" id="templateIntervalUnit" placeholder="Interval Unit" value={this.state.input.intervalUnit} onChange={this.handleChange}>
                  <option value='' disabled>Select Interval Unit</option>
                  {['Hour', 'Day', 'Week', 'Month', 'Year', 'Mile', 'Trip'].map(item => <option value={item} disabled={['Year', 'Mile', 'Trip'].includes(item)}>{item}</option>)}
                </Input>
              </FormGroup>
              </Col>
              <Col xs={12}>
                <FormGroup>
                  <Label for="carModel">Car Model</Label>
                  <Select
                    isLoading={!(this.props.allCarModels && this.props.allCarModels.edges && this.props.allCarModels.edges.length > 0)}
                    isMulti
                    value={this.state.selectedCarModels}
                    options={this.props.allCarModels && this.props.allCarModels.edges.length > 0 && this.props.allCarModels.edges.filter(item => item.node.make && item.node.name).map((item) => ({
                      value: item.node.id, label: toTitleCase(item.node.make) + " " + item.node.name + (item.node.series ? " | " + item.node.series : "") + (item.node.groupType ? " | " + item.node.groupType.name : " | No Group Selected")
                    }))}
                    placeholder="Select Model"
                    onChange={this.handleCarModelChange}
                  />
                </FormGroup>
              </Col>
              <Col xs={6}>
                <FormGroup>
                  <Label for="carYearMin">Car Year Min</Label>
                  <Input type="number" name="carYearMin" id="carYearMin" placeholder="Car Year Minimum" min={2000} max={3000} value={this.state.input.carYearMin} onChange={this.handleChange} />
                </FormGroup>
              </Col>
              <Col xs={6}>
                <FormGroup>
                  <Label for="carYearMax">Car Year Max</Label>
                  <Input type="number" name="carYearMax" id="carYearMax" placeholder="Car Year Maximum" min={this.state.input.carYearMin ? this.state.input.carYearMin : 2000} max={3000} value={this.state.input.carYearMax} onChange={this.handleChange} />
                </FormGroup>
              </Col>
              <Col xs={2}>
                <FormGroup>
                  <Label for="mileageUnit">Unit</Label>
                  <Input type="text" name="mileageUnit" id="mileageUnit" disabled value={this.state.defaultMileageUnit} />
                  <UncontrolledTooltip placement="top" target="mileageUnit">{this.state.defaultMileageUnit === "MI" ? "Miles" : "Kilometers"}</UncontrolledTooltip>
                </FormGroup>
              </Col>
              <Col xs={5}>
                <FormGroup>
                  <Label for="carMileageMin">Car Mileage Min</Label>
                  <Input
                    type="number"
                    name="carMileageMin"
                    id="carMileageMin"
                    placeholder={`Car Minimum ${this.state.defaultMileageUnit === "MI" ? "Miles" : "Kilometers"}`}
                    min={0}
                    value={this.state.input.carMileageMin}
                    onChange={this.handleChange}
                  />
                </FormGroup>
              </Col>
              <Col xs={5}>
                <FormGroup>
                  <Label for="carMileageMax">Car Mileage Max</Label>
                  <Input
                    type="number"
                    name="carMileageMax"
                    id="carMileageMax"
                    placeholder={`Car Maximum ${this.state.defaultMileageUnit === "MI" ? "Miles" : "Kilometers"}`}
                    min={this.state.input.carMileageMin ? this.state.input.carMileageMin : 0}
                    value={this.state.input.carMileageMax}
                    onChange={this.handleChange}
                  />
                </FormGroup>
              </Col>
              <Col xs={6}>
                <FormGroup check>
                  <Label check>
                    <Input
                      name="isDefault"
                      placeholder="Is Default"
                      type="checkbox"
                      checked={this.state.input.isDefault}
                      onChange={this.handleChange}
                    />
                    {" "}
                    Is Default
                  </Label>
                </FormGroup>
              </Col>
              <Col xs={6}>
                <FormGroup check>
                  <Label check>
                    <Input
                      name='visibleToCustomers'
                      placeholder="Visible To Customers"
                      type="checkbox"
                      checked={this.state.input.visibleToCustomers}
                      onChange={this.handleChange}
                    />
                    {" "}
                    Visible to Customers
                  </Label>
                </FormGroup>
              </Col>
            </Row>
            <br />
            {this.state.error && (
              <small style={{ marginBottom: '10px', color: 'red', display: 'block', whiteSpace: 'pre-wrap' }}>
                {this.state.error}
              </small>
            )}
            <ModalFooter>
              {this.state.loading ? "Loading..." : <>
                <FormGroup check>
                  <Label check>
                    <Input
                      name="createAnother"
                      type="checkbox"
                      checked={this.state.createAnother}
                      onChange={() => this.setState({ createAnother: !this.state.createAnother })}
                    />
                    {" "}
                    Create another
                  </Label>
                </FormGroup>
                <Button color="primary" type="submit">Create Price</Button>
              </>
              }
              <Button color="secondary" onClick={() => this.props.toggleModal()}>Close</Button>
            </ModalFooter>
          </Form>
        </ModalBody>
        {this.state.showConfirmationModal && (
          <PricingConfirmationModal
            isOpen={this.state.showConfirmationModal}
            toggle={() => this.setState({ showConfirmationModal: false })}
            onConfirm={this.handleConfirmCreate}
            pricingSummary={this.buildPricingSummary()}
            loading={this.state.loading}
          />
        )}
      </Modal>
    )
  }
}


//-----------------------------------
export default graphql(AllCarModels, {
  options: { fetchPolicy: "cache-first" },
  props: ({ data: { loading, allCarModels } }) => ({ loading, allCarModels })
})(
  graphql(AllAgreementTypes, {
    options: { fetchPolicy: "cache-first" },
    props: ({ data: { loading, allAgreementTypes } }) => ({ loading, allAgreementTypes })
  })(
    graphql(UserSelectedBranchesQuery, {
      options: { fetchPolicy: "cache-first" },
      props: ({ data: { loading, currentUser } }) => ({
        loadingUserBranches: loading,
        userSelectedBranches: currentUser?.member?.selectedBranches
      })
    })(withApolloClient(AddPricingModal))
  )
)
